import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Layout,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import API from '../config/api';
import { db } from '../firebaseConfig';
import DashboardHeader from './DashboardHeader';
import DashboardSidebar from './DashboardSidebar';
import { useLanguage } from './LanguageContext';

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface DoctorOption {
  doctorId: string;
  doctorName: string;
}

const PAGE_LIMIT = 100;

const safeText = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatDate = (value: any): string => {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : safeText(value);
};

const formatDateTime = (value: any): string => {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : safeText(value);
};

const arrayText = (value: any): string => {
  if (!Array.isArray(value)) return '';
  return value.map((item) => safeText(item)).filter(Boolean).join(' | ');
};

const patientKey = (patient: any): string => {
  const file = safeText(patient.file_number || patient.file_id);
  const phone = safeText(patient.normalized_phone || patient.patient_phone);
  const pid = safeText(patient.patient_id || patient._id);
  return `${file || pid}::${phone || pid}`;
};

const rawJsonColumns = (value: any) => {
  const raw = JSON.stringify(value || {});
  const chunks: Record<string, string> = {};
  const chunkSize = 30000;
  for (let i = 0; i < Math.min(6, Math.ceil(raw.length / chunkSize)); i += 1) {
    chunks[`raw_json_${i + 1}`] = raw.slice(i * chunkSize, (i + 1) * chunkSize);
  }
  return chunks;
};

const PatientDataExport: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<string[]>([]);
  const [allDoctors, setAllDoctors] = useState(true);
  const [allDates, setAllDates] = useState(true);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const navigate = useNavigate();
  const clinicId = localStorage.getItem('clinicId') || '';
  const ownerDoctorId = localStorage.getItem('doctorId') || '';
  const { t, language, isRTL } = useLanguage();

  const loadDoctors = useCallback(async () => {
    if (!clinicId) return;
    setLoadingDoctors(true);
    try {
      const dcaSnap = await getDocs(
        query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
      );
      const doctorIds = Array.from(
        new Set(dcaSnap.docs.map((d) => d.data().doctor_id as string).filter(Boolean))
      );

      const nameMap = new Map<string, string>();
      for (let i = 0; i < doctorIds.length; i += 30) {
        const batch = doctorIds.slice(i, i + 30);
        const snap = await getDocs(query(collection(db, 'doctors'), where('doctor_id', 'in', batch)));
        snap.docs.forEach((d) => {
          const data = d.data();
          nameMap.set(data.doctor_id, data.name || data.email || data.doctor_id);
        });
      }

      const nextDoctors = doctorIds.map((doctorId) => ({
        doctorId,
        doctorName: nameMap.get(doctorId) || doctorId,
      }));
      setDoctors(nextDoctors);
      setSelectedDoctorIds(nextDoctors.map((d) => d.doctorId));
    } catch (err) {
      console.error('Failed to load doctors for export:', err);
      message.error(t('patientExportLoadDoctorsFailed'));
    } finally {
      setLoadingDoctors(false);
    }
  }, [clinicId, t]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  const selectedDoctors = useMemo(() => {
    if (allDoctors) return doctors;
    return doctors.filter((d) => selectedDoctorIds.includes(d.doctorId));
  }, [allDoctors, doctors, selectedDoctorIds]);

  const fetchPatientPage = async (page: number, doctorId?: string) => {
    const params: Record<string, any> = {
      page,
      limit: PAGE_LIMIT,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      bypassCache: 'true',
    };

    if (clinicId) {
      params.clinic_id = clinicId;
    }

    if (doctorId) {
      params.doctor_id = doctorId;
    } else if (allDoctors && clinicId) {
      params.use_clinic_scope = 'true';
      params.clinic_ids = clinicId;
    } else if (ownerDoctorId) {
      params.doctor_id = ownerDoctorId;
    }

    if (!allDates) {
      params.startDate = dateRange[0]?.startOf('day').format('YYYY-MM-DD');
      params.endDate = dateRange[1]?.endOf('day').format('YYYY-MM-DD');
    }

    const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENTS}`, {
      params,
      timeout: 60000,
    });
    if (!res.data?.success) {
      throw new Error(res.data?.message || 'Failed to fetch patients');
    }
    return res.data;
  };

  const fetchPatients = async () => {
    const allRows: any[] = [];
    const seen = new Set<string>();
    const doctorTargets = allDoctors ? [undefined] : selectedDoctors.map((d) => d.doctorId);

    for (let targetIndex = 0; targetIndex < doctorTargets.length; targetIndex += 1) {
      const doctorId = doctorTargets[targetIndex];
      let page = 1;
      let totalPages = 1;

      do {
        const data = await fetchPatientPage(page, doctorId);
        const rows = Array.isArray(data.data) ? data.data : [];
        rows.forEach((patient: any) => {
          const key = patientKey(patient);
          if (!seen.has(key)) {
            seen.add(key);
            allRows.push(patient);
          }
        });
        totalPages = Number(data.pagination?.totalPages || 1);
        const baseProgress = doctorTargets.length > 0 ? targetIndex / doctorTargets.length : 0;
        const pageProgress = totalPages > 0 ? page / totalPages / doctorTargets.length : 0;
        setProgress(Math.min(90, Math.round((baseProgress + pageProgress) * 90)));
        page += 1;
      } while (page <= totalPages);
    }

    return allRows;
  };

  const buildWorkbook = (patients: any[]) => {
    const patientsSheet: any[] = [];
    const visitsSheet: any[] = [];
    const prescriptionsSheet: any[] = [];
    const drugsSheet: any[] = [];
    const complaintsSheet: any[] = [];
    const obgynSheet: any[] = [];
    const externalSheet: any[] = [];
    const voiceSheet: any[] = [];
    const rawSheet: any[] = [];

    patients.forEach((p) => {
      const visits = Array.isArray(p.visits) ? p.visits : [];
      const obgyn = p.obgynHistory || {};
      const baseRef = {
        file_number: safeText(p.file_number || p.file_id),
        patient_id: safeText(p.patient_id),
        patient_name: safeText(p.patient_name),
        patient_phone: safeText(p.patient_phone),
      };

      patientsSheet.push({
        ...baseRef,
        age: safeText(p.age),
        address: safeText(p.address),
        doctor_name: safeText(p.doctor_name),
        doctor_id: safeText(p.doctor_id),
        clinic_id: safeText(p.clinic_id),
        status: safeText(p.status),
        visit_type: safeText(p.visit_type),
        total_visits: p.total_visits ?? visits.length,
        latest_visit_date: formatDate(p.date || visits[0]?.date),
        latest_visit_time: safeText(p.time || visits[0]?.time),
        insurance_company: safeText(p.insurance_company_name),
        insurance_number: safeText(p.insurance_number),
        coverage_percentage: safeText(p.coverage_percentage),
        payment_type: safeText(p.payment_type),
        created_at: formatDateTime(p.createdAt),
        updated_at: formatDateTime(p.updatedAt),
      });

      visits.forEach((visit: any, visitIndex: number) => {
        const visitId = safeText(visit.visit_id || visit._id || `${baseRef.patient_id}-${visitIndex + 1}`);
        visitsSheet.push({
          ...baseRef,
          visit_id: visitId,
          visit_date: formatDate(visit.date),
          visit_time: safeText(visit.time),
          visit_type: safeText(visit.visit_type),
          visit_speed: safeText(visit.visit_speed),
          status: safeText(visit.status),
          complaint: safeText(visit.complaint),
          diagnosis: safeText(visit.diagnosis),
          procedure: safeText(visit.procedure),
          doctor_name: safeText(visit.doctor_name || p.doctor_name),
          doctor_id: safeText(visit.doctor_id || p.doctor_id),
          receipts_count: Array.isArray(visit.receipts) ? visit.receipts.length : 0,
        });

        (visit.receipts || []).forEach((receipt: any, receiptIndex: number) => {
          const receiptId = safeText(receipt._id || receipt.receipt_id || `${visitId}-rx-${receiptIndex + 1}`);
          prescriptionsSheet.push({
            ...baseRef,
            visit_id: visitId,
            receipt_id: receiptId,
            receipt_date: formatDateTime(receipt.date),
            prescription_template: safeText(receipt.prescriptionTemplate),
            drug_model: safeText(receipt.drugModel),
            notes: safeText(receipt.notes),
            investigations: arrayText(receipt.investigations),
            next_visit_date: formatDate(receipt.nextVisitDate),
            diagnosis: safeText(receipt.diagnosis),
            drugs_count: Array.isArray(receipt.drugs) ? receipt.drugs.length : 0,
          });

          (receipt.drugs || []).forEach((drug: any, drugIndex: number) => {
            drugsSheet.push({
              ...baseRef,
              visit_id: visitId,
              receipt_id: receiptId,
              drug_index: drugIndex + 1,
              drug: safeText(drug.drug),
              frequency: safeText(drug.frequency),
              period: safeText(drug.period),
              timing: safeText(drug.timing),
              form: safeText(drug.form),
              route: safeText(drug.route),
              dose_quantity: safeText(drug.doseQty),
              medicine_details: safeText(drug.medicineDetails),
            });
          });
        });
      });

      (p.complaint_history || []).forEach((entry: any, index: number) => {
        complaintsSheet.push({
          ...baseRef,
          entry_index: index + 1,
          date: formatDate(entry.date),
          complaint: safeText(entry.complaint),
          diagnosis: safeText(entry.diagnosis),
          procedure: safeText(entry.procedure),
          doctor_id: safeText(entry.doctor_id),
          deleted_at: formatDateTime(entry.deletedAt),
        });
      });

      obgynSheet.push({
        ...baseRef,
        married_since: safeText(obgyn.marriedSince),
        gravidity: safeText(obgyn.gravidity),
        parity: safeText(obgyn.parity),
        living: safeText(obgyn.living),
        abortion: safeText(obgyn.abortion),
        deaths: safeText(obgyn.deaths),
        delivery_type: safeText(obgyn.deliveryType),
        delivery_since: safeText(obgyn.deliverySince),
        lmp: safeText(obgyn.lmp),
        edd: safeText(obgyn.edd),
        medications: safeText(obgyn.medications),
        past_history: safeText(obgyn.pastHistory),
        surgical_history: safeText(obgyn.surgicalHistory),
        husband_name: safeText(obgyn.husbandName),
        husband_age: safeText(obgyn.husbandAge),
        husband_job: safeText(obgyn.husbandJob),
        husband_residency: safeText(obgyn.husbandResidency),
        family_history: safeText(obgyn.familyHistory),
        consanguinity: safeText(obgyn.consanguinity),
        blood_group: safeText(obgyn.bloodGroup),
        blood_transfusion: safeText(obgyn.bloodTransfusion),
        blood_pressure: safeText(obgyn.bloodPressure),
        weight: safeText(obgyn.weight),
        temperature: safeText(obgyn.temperature),
      });

      (p.externalServiceRequests || []).forEach((service: any, index: number) => {
        externalSheet.push({
          ...baseRef,
          request_index: index + 1,
          service_name: safeText(service.service_name),
          provider_name: safeText(service.provider_name),
          status: safeText(service.status),
          requested_at: formatDateTime(service.requestedAt),
        });
      });

      (p.voice_notes || []).forEach((note: any, index: number) => {
        voiceSheet.push({
          ...baseRef,
          note_index: index + 1,
          text: safeText(note.text),
          created_at: formatDateTime(note.createdAt),
          updated_at: formatDateTime(note.updatedAt),
          audio_url: safeText(note.audioUrl),
          has_inline_audio: note.audioData ? 'yes' : 'no',
        });
      });

      rawSheet.push({
        ...baseRef,
        ...rawJsonColumns(p),
      });
    });

    const workbook = XLSX.utils.book_new();
    const append = (rows: any[], name: string) => {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows.length ? rows : [{}]), name);
    };

    append(patientsSheet, 'Patients');
    append(visitsSheet, 'Visits');
    append(prescriptionsSheet, 'Prescriptions');
    append(drugsSheet, 'Drugs');
    append(complaintsSheet, 'Complaints');
    append(obgynSheet, 'OBGYN');
    append(externalSheet, 'External Services');
    append(voiceSheet, 'Voice Notes');
    append(rawSheet, 'Raw JSON');

    return workbook;
  };

  const handleExport = async () => {
    if (!clinicId) {
      message.error(t('patientExportMissingClinic'));
      return;
    }
    if (!allDoctors && selectedDoctorIds.length === 0) {
      message.error(t('patientExportSelectDoctor'));
      return;
    }
    if (!allDates && (!dateRange[0] || !dateRange[1])) {
      message.error(t('patientExportSelectRange'));
      return;
    }

    setExporting(true);
    setProgress(3);
    try {
      const patients = await fetchPatients();
      setProgress(94);
      const workbook = buildWorkbook(patients);
      const dateLabel = allDates
        ? 'all-dates'
        : `${dateRange[0]?.format('YYYY-MM-DD')}_to_${dateRange[1]?.format('YYYY-MM-DD')}`;
      const doctorLabel = allDoctors ? 'all-doctors' : `${selectedDoctorIds.length}-doctors`;
      XLSX.writeFile(workbook, `patient-export_${doctorLabel}_${dateLabel}.xlsx`);
      setProgress(100);
      message.success(t('patientExportSuccess'));
    } catch (err: any) {
      console.error('Patient export failed:', err);
      message.error(err?.message || t('patientExportFailed'));
    } finally {
      setExporting(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const previewColumns = [
    { title: t('patientExportDoctor'), dataIndex: 'doctorName', key: 'doctorName' },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout>
        <DashboardHeader onSettingsClick={() => navigate('/settings')} onMenuClick={() => {}} isMobile={false} />
        <Content style={{ margin: '24px 16px', padding: 24 }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
            <Col>
              <Space align="center">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')} type="text" />
                <FileExcelOutlined style={{ fontSize: 22, color: '#1f9d55' }} />
                <Title level={3} style={{ margin: 0 }}>
                  {t('patientExportTitle')}
                </Title>
              </Space>
              <Text type="secondary" style={{ display: 'block', marginTop: 4, marginInlineStart: 52 }}>
                {t('patientExportSubtitle')}
              </Text>
            </Col>
            <Col>
              <Button icon={<ReloadOutlined />} onClick={loadDoctors} loading={loadingDoctors}>
                {t('patientExportRefresh')}
              </Button>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{t('patientExportDoctorScope')}</Text>
                    <Row gutter={[12, 12]} style={{ marginTop: 10 }}>
                      <Col xs={24} md={8}>
                        <Checkbox checked={allDoctors} onChange={(e) => setAllDoctors(e.target.checked)}>
                          {t('patientExportAllDoctors')}
                        </Checkbox>
                      </Col>
                      <Col xs={24} md={16}>
                        <Select
                          mode="multiple"
                          disabled={allDoctors}
                          loading={loadingDoctors}
                          value={selectedDoctorIds}
                          onChange={setSelectedDoctorIds}
                          placeholder={t('patientExportSelectDoctors')}
                          style={{ width: '100%' }}
                          options={doctors.map((d) => ({ value: d.doctorId, label: d.doctorName }))}
                          maxTagCount="responsive"
                        />
                      </Col>
                    </Row>
                  </div>

                  <div>
                    <Text strong>{t('patientExportDateScope')}</Text>
                    <Row gutter={[12, 12]} style={{ marginTop: 10 }}>
                      <Col xs={24} md={8}>
                        <Checkbox checked={allDates} onChange={(e) => setAllDates(e.target.checked)}>
                          {t('patientExportAllDates')}
                        </Checkbox>
                      </Col>
                      <Col xs={24} md={16}>
                        <RangePicker
                          disabled={allDates}
                          value={dateRange}
                          onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
                          style={{ width: '100%' }}
                        />
                      </Col>
                    </Row>
                  </div>

                  {exporting && <Progress percent={progress} status={progress >= 100 ? 'success' : 'active'} />}

                  <Button
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                    loading={exporting}
                    disabled={loadingDoctors || doctors.length === 0}
                  >
                    {t('patientExportDownload')}
                  </Button>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Card>
                    <Statistic
                      title={t('patientExportDoctorsIncluded')}
                      value={selectedDoctors.length}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          <Card title={t('patientExportIncludedDoctors')} style={{ marginTop: 16 }}>
            {selectedDoctors.length === 0 ? (
              <Empty description={t('patientExportNoDoctors')} />
            ) : (
              <Table
                size="small"
                rowKey="doctorId"
                columns={previewColumns}
                dataSource={selectedDoctors}
                pagination={{ pageSize: 5 }}
                direction={isRTL ? 'rtl' : 'ltr'}
              />
            )}
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Space align="start">
              <Avatar icon={<UserOutlined />} />
              <div>
                <Text strong>{t('patientExportWorkbookTitle')}</Text>
                <div>
                  <Text type="secondary">
                    {language === 'ar'
                      ? 'Patients, Visits, Prescriptions, Drugs, Complaints, OBGYN, External Services, Voice Notes, Raw JSON'
                      : 'Patients, Visits, Prescriptions, Drugs, Complaints, OBGYN, External Services, Voice Notes, Raw JSON'}
                  </Text>
                </div>
              </div>
            </Space>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default PatientDataExport;
